import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Button,
  CloseButton,
  LoadingState,
  SectionHeader,
  TransitionEffect,
  Typography,
} from "~/common/components";
import { SchedulingMethodsTable } from "~/common/components/SchedulingMethodsTable";
import { useGetCurrentPatient } from "../hooks";
import { useCompleteSchedule } from "../hooks/scheduling";
import { ROUTES } from "../router";

export const SchedulingInstructions = () => {
  const navigate = useNavigate();

  const { data: patient } = useGetCurrentPatient();

  const queryClient = useQueryClient();

  const onSuccess = async () => {
    navigate(ROUTES.leadPatientHome);
    toast.success("Appointment scheduled successfully");
    await queryClient.invalidateQueries({
      queryKey: ["getCurrentPatient"],
    });
  };

  const { completeScheduleMutation, isPending } =
    useCompleteSchedule(onSuccess);

  const schedulingMethod = useMemo(
    () =>
      patient?.organization_patients?.[0]?.organization
        ?.scheduling_methods?.[0],
    [patient],
  );

  if (!patient) return <LoadingState />;

  const handleMarkAsDone = () => {
    completeScheduleMutation(patient?.consultation?.id);
  };

  return (
    <TransitionEffect className="flex justify-center">
      <div className="flex h-screen-calculated w-full flex-col gap-5 overflow-y-auto p-6 text-sm lg:w-1/2 lg:py-8">
        <CloseButton onClose={() => navigate(-1)} />
        <div className="flex flex-col gap-11">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
              <SectionHeader className="text-4xl">
                Schedule appointment
              </SectionHeader>
            </div>
            <Typography>
              Please schedule an appointment using one of the following options:
            </Typography>

            <SchedulingMethodsTable data={schedulingMethod} />

            <Typography>
              Once your appointment is scheduled, click below to mark this step
              as done.
            </Typography>
          </div>
          <Button isLoading={isPending} onClick={handleMarkAsDone}>
            Mark as done
          </Button>
        </div>
      </div>
    </TransitionEffect>
  );
};
